import type {DAL, DocumentManager} from '@wix/document-manager-core'
import type {PageExtensionAPI, PageAPI} from '../../../page'
import type {PageMigrator} from '../dataMigrationRunner'
import type {Pointer} from '@wix/document-services-types'
import type {MultilingualExtensionAPI} from '../../../multilingual'
import * as constants from '../../../../constants/constants'
const {MASTER_PAGE_ID} = constants

interface FixedPageUriSEODetails {
    before: string
    after: string
}

const fixPageUriSEOIfInvalid = (
    dal: DAL,
    page: PageAPI,
    pageId: string,
    pointer: Pointer,
    mainLanguageFixDetails?: FixedPageUriSEODetails,
    languageCode?: string
): FixedPageUriSEODetails | undefined => {
    const pageData = dal.get(pointer)
    if (pageData) {
        const currentPageUriSEO = pageData.pageUriSEO
        if (languageCode && currentPageUriSEO === mainLanguageFixDetails?.before) {
            dal.set(pointer, {...pageData, pageUriSEO: mainLanguageFixDetails!.after})
            return
        }
        const validPageUriSEO = page.getValidPageUriSEO(pageId, currentPageUriSEO, languageCode)
        if (validPageUriSEO !== currentPageUriSEO) {
            dal.set(pointer, {...pageData, pageUriSEO: validPageUriSEO})
            return {before: currentPageUriSEO, after: validPageUriSEO}
        }
    }
}

// this migrator is a PageMigrator, but it touches the data on the masterPage.
// Technically it should be a MasterPageMigrator,
// but due to dependencies with another migrator and having the ability to run this in DMS, it needs to be a PageMigrator
const migratePage = (documentManager: DocumentManager, pageId: string) => {
    if (pageId !== MASTER_PAGE_ID) {
        const {page, multilingualTranslations} = documentManager.extensionAPI as PageExtensionAPI &
            MultilingualExtensionAPI
        const allTranslationLanguages = multilingualTranslations.getAllTranslationLanguages()

        const {dal, pointers} = documentManager
        const mainLanguageFixDetails = fixPageUriSEOIfInvalid(
            dal,
            page,
            pageId,
            pointers.data.getDataItemFromMaster(pageId)
        )

        allTranslationLanguages.forEach(languageCode => {
            fixPageUriSEOIfInvalid(
                dal,
                page,
                pageId,
                pointers.multilingualTranslations.translationDataItem(MASTER_PAGE_ID, languageCode, pageId),
                mainLanguageFixDetails,
                languageCode
            )
        })
    }
}

const name = 'fixInvalidPageUriSEO'
const version = 3

export const fixInvalidPageUriSEO: PageMigrator = {
    migratePage,
    name,
    version
}
